<template>
  <div class="flex flex-col gap-2">
    <div class="relative h-40 w-full overflow-hidden">
      <div ref="chartRef" class="h-full w-full" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useDark, useDebounceFn } from '@vueuse/core'
  import dayjs from 'dayjs'
  import * as echarts from 'echarts/core'
  import { HeatmapChart } from 'echarts/charts'
  import {
    CalendarComponent,
    TooltipComponent,
    VisualMapComponent,
  } from 'echarts/components'
  import { CanvasRenderer } from 'echarts/renderers'
  import { displayDate } from '@/utils/formatter/date'
  import type { DailyUsagePoint } from '@/client'
  import {
    HEATMAP_PAST_DAYS,
    HEATMAP_BLUE_THEME,
    HEATMAP_BLUE_THEME_DARK,
  } from '@/stores/browserextension/constants'

  echarts.use([
    HeatmapChart,
    CalendarComponent,
    TooltipComponent,
    VisualMapComponent,
    CanvasRenderer,
  ])

  const props = defineProps<{
    data: DailyUsagePoint[]
    fromDate?: Date | null
    tillDate?: Date | null
  }>()

  const i18n = useI18n()
  const isDark = useDark()
  const chartRef = ref<HTMLDivElement | null>(null)
  const chartInstance = ref<echarts.ECharts | null>(null)

  const elevationColor = computed(function () {
    if (typeof window === 'undefined') {
      return '#1f2937'
    }

    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--sm-elevation')
      .trim()

    return color || '#1f2937'
  })

  const heatmapTheme = computed(function () {
    if (isDark.value) {
      return {
        emptyCellColor: '#353638',
        cellGapColor: elevationColor.value,
        monthLabelColor: '#9ca3af',
        dayLabelColor: '#6b7280',
        colorScale: [...HEATMAP_BLUE_THEME_DARK].slice(1),
      }
    }

    return {
      emptyCellColor: '#f3f4f6',
      cellGapColor: '#ffffff',
      monthLabelColor: '#6b7280',
      dayLabelColor: '#9ca3af',
      colorScale: [...HEATMAP_BLUE_THEME].slice(1),
    }
  })

  // Data Processing
  const chartData = computed(() => {
    if (!props.data) return []
    return props.data.map((point) => [
      dayjs.unix(point.timestamp).format('YYYY-MM-DD'),
      point.count,
    ])
  })

  const range = computed(() => {
    // If explicit dates are provided, use them
    if (props.fromDate && props.tillDate) {
      return [
        dayjs(props.fromDate).format('YYYY-MM-DD'),
        dayjs(props.tillDate).format('YYYY-MM-DD'),
      ]
    }

    // Fallback: Last 90 days from today (or tillDate if provided)
    const end = props.tillDate ? dayjs(props.tillDate) : dayjs().endOf('day')
    const start = props.fromDate
      ? dayjs(props.fromDate)
      : end.subtract(HEATMAP_PAST_DAYS, 'day').startOf('week')

    return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
  })

  const maxValue = computed(() => {
    if (!props.data?.length) return 5 // Default scale
    return Math.max(...props.data.map((p) => p.count), 5)
  })

  // ECharts Option
  const getOption = (): echarts.EChartsCoreOption => {
    return {
      backgroundColor: 'transparent',
      tooltip: {
        position: 'top',
        formatter: (params: { data: [string, number] }) => {
          const date = params.data[0]
          const count = Number(params.data[1] ?? 0)
          return `
            <div class="font-medium text-xs">${displayDate(new Date(date))}</div>
            <div class="text-xs text-gray-300">${i18n.t('browserExtension.sessionCount', { count })}</div>
          `
        },
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        padding: [6, 8],
        borderRadius: 4,
        borderWidth: 0,
        textStyle: { color: '#fff' },
      },
      visualMap: {
        min: 0,
        max: maxValue.value,
        show: false, // Hide built-in legend, we use custom one
        inRange: {
          color: heatmapTheme.value.colorScale,
        },
      },
      calendar: {
        top: 35,
        left: 30,
        right: 10,
        bottom: 10,
        cellSize: ['auto', 13], // Square-ish cells
        range: range.value,
        itemStyle: {
          color: heatmapTheme.value.emptyCellColor, // Default color for empty days
          borderWidth: 3,
          borderColor: heatmapTheme.value.cellGapColor, // Gap between cells
        },
        splitLine: { show: false },
        yearLabel: { show: false },
        monthLabel: {
          nameMap: 'en',
          fontSize: 12,
          color: heatmapTheme.value.monthLabelColor,
          margin: 10,
        },
        dayLabel: {
          firstDay: 1, // Start week on Monday
          nameMap: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          fontSize: 10,
          color: heatmapTheme.value.dayLabelColor,
        },
      },
      series: {
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: chartData.value,
        itemStyle: {
          borderRadius: 2, // Rounded corners on cells
        },
      },
    }
  }

  // Lifecycle & Rendering
  const renderChart = () => {
    if (!chartRef.value) return

    if (!chartInstance.value) {
      chartInstance.value = echarts.init(chartRef.value, null, {
        renderer: 'canvas',
      })
    }

    chartInstance.value.setOption(getOption())
  }

  const debouncedResize = useDebounceFn(
    () => chartInstance.value?.resize(),
    200
  )

  onMounted(async () => {
    await nextTick()
    renderChart()
    window.addEventListener('resize', debouncedResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', debouncedResize)
    chartInstance.value?.dispose()
  })

  watch([() => props.data, isDark], renderChart, { deep: true })
</script>
